// Chrome's Google Translate moves or replaces text nodes under <font> wrappers it inserts, so React's
// commit phase calls removeChild/insertBefore on nodes that are no longer where it recorded them and
// the DOM throws NotFoundError, blanking the page. Reconcile against the wrapper instead of blocking
// translation. Only mismatches Translate can explain are absorbed; anything else still throws.
const FONT_TAG = 'FONT';

// Only reconcile on a translated page, so an untranslated session keeps the native semantics exactly.
// Read on the mismatch path only, never on the hot path.
const isTranslated = (): boolean => {
  const { classList } = document.documentElement;
  return classList.contains('translated-ltr') || classList.contains('translated-rtl');
};

const directChildOf = (parent: Node, node: Node): Node | null => {
  let current: Node | null = node;
  while (current && current.parentNode !== parent) {
    current = current.parentNode;
  }
  return current;
};

export const installTranslateDomCompat = (): void => {
  if (typeof Node !== 'function' || !Node.prototype) {
    return;
  }

  const nodeProto = Node.prototype as typeof Node.prototype & { __columnTranslateCompat?: boolean };
  if (nodeProto.__columnTranslateCompat) {
    return;
  }
  Object.defineProperty(nodeProto, '__columnTranslateCompat', { value: true });

  const originalRemoveChild = nodeProto.removeChild;
  const originalInsertBefore = nodeProto.insertBefore;

  const pruneEmptyWrappers = (from: Node, stopAt: Node): void => {
    let node: Node | null = from;
    while (node && node !== stopAt && node.nodeName === FONT_TAG && node.childNodes.length === 0) {
      const parent: Node | null = node.parentNode;
      if (!parent) {
        return;
      }
      originalRemoveChild.call(parent, node);
      node = parent;
    }
  };

  nodeProto.removeChild = function removeChildCompat<T extends Node>(this: Node, child: T): T {
    const actualParent = child.parentNode;
    if (actualParent === this || !isTranslated()) {
      return originalRemoveChild.call(this, child) as T;
    }

    if (!actualParent) {
      return child;
    }

    if (directChildOf(this, child)) {
      const removed = originalRemoveChild.call(actualParent, child) as T;
      pruneEmptyWrappers(actualParent, this);
      return removed;
    }

    return originalRemoveChild.call(this, child) as T;
  };

  nodeProto.insertBefore = function insertBeforeCompat<T extends Node>(this: Node, node: T, reference: Node | null): T {
    if (!reference || reference.parentNode === this || !isTranslated()) {
      return originalInsertBefore.call(this, node, reference) as T;
    }

    const anchor = directChildOf(this, reference);
    if (anchor) {
      return originalInsertBefore.call(this, node, anchor) as T;
    }

    if (!reference.parentNode) {
      return originalInsertBefore.call(this, node, null) as T;
    }

    return originalInsertBefore.call(this, node, reference) as T;
  };
};
